import React, { useEffect, FC } from 'react'
import { connect } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { FeedbackMessage } from 'components/marketplace/FeedbackMessage'
import { Tabs, Tab } from 'transfix-ui/components/DeprecatedTabs'
import { Spinner } from 'transfix-ui/components/Spinner'
import { getParentDriverLocations } from 'utils/actions/driverLocations'
import { fetchChildUsers, getParentUserProfileDetails } from 'utils/actions/user'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import {
  USER_PROFILE_TABS,
  SHIPMENTS_TAB,
  TRACKING_TAB,
  DRIVER_LOCATIONS_TAB,
  LOGS_TAB,
} from 'apps/user/constants/userProfile'
import DriverLocations from '../DriverLocations'
import Logs from '../Logs'
import Shipments from '../Shipments'
import { Tracking } from '../Tracking'
import { UserOverview } from '../UserOverview/UserOverview'
import { DeleteUserDialog } from './DeleteUserDialog'
import { ImpersonateUserDialog } from './ImpersonateUserDialog'
import { UserProfileHeader as UserProfileHeaderComponent } from './UserProfileHeader/UserProfileHeader'
import { useImpersonation } from './hooks/useImpersonation'

interface IUserProps {
  fetching: boolean
  error: string
  fetchChildUsers: Function
  getParentUserProfileDetails: Function
  getParentDriverLocations: Function
  isDriver: boolean | null
  isOwnerOperator: boolean | null
}

const UserComponent: FC<IUserProps> = ({
  error,
  fetchChildUsers,
  fetching,
  getParentDriverLocations,
  getParentUserProfileDetails,
  isDriver,
  isOwnerOperator,
}) => {
  const { id, tab = '' } = useParams<{
    id: string
    tab: string
  }>()
  const history = useHistory()

  const {
    hideImpersonateUserDialog,
    impersonateError,
    impersonateParentUser,
    showImpersonateUserDialog,
    userIdToImpersonate,
  } = useImpersonation()

  useEffect(() => {
    if (id) {
      getParentUserProfileDetails(id)
      fetchChildUsers(id)
    }
  }, [fetchChildUsers, getParentUserProfileDetails, id])

  useEffect(() => {
    if (isDriver || isOwnerOperator) {
      getParentDriverLocations(id, 20)
    }
  }, [getParentDriverLocations, id, isDriver, isOwnerOperator])

  const changeTab = (newTab: keyof typeof USER_PROFILE_TABS) => {
    const path = USER_PROFILE_TABS[newTab]
    history.push(path ? `/${id}/${path}` : `/${id}`)
  }

  const showDriverLocations = __ENV__ === 'staging' || isDriver || isOwnerOperator
  const ready = !fetching && !error

  const { getFlag } = useFeatureFlagContext()
  const { enabled: marketplaceUserTrackingTab } = getFlag('marketplaceUserTrackingTab')

  // TODO: Fix types to avoid doing this
  const UserProfileHeader = UserProfileHeaderComponent as any

  return (
    <div className='container'>
      {fetching && <Spinner />}
      {error && <FeedbackMessage message={error} type='error' />}
      {ready && (
        <>
          <UserProfileHeader showImpersonateUserDialog={showImpersonateUserDialog} />
          <Tabs dataTest='user-tabs' value={tab || 'User Overview'} onChange={changeTab}>
            <Tab label='User Overview'>
              <UserOverview />
            </Tab>
            <Tab label='Shipments' value={SHIPMENTS_TAB}>
              <Shipments />
            </Tab>
            {marketplaceUserTrackingTab && (
              <Tab label='Tracking' value={TRACKING_TAB}>
                <Tracking parentUserID={parseInt(id, 10)} />
              </Tab>
            )}
            {showDriverLocations && (
              <Tab label='Driver Locations' value={DRIVER_LOCATIONS_TAB}>
                <DriverLocations />
              </Tab>
            )}
            <Tab label='Logs' value={LOGS_TAB}>
              <Logs />
            </Tab>
          </Tabs>
          <DeleteUserDialog />
          <ImpersonateUserDialog
            hideImpersonateUserDialog={hideImpersonateUserDialog}
            impersonateError={impersonateError}
            showImpersonateDialogForUserId={userIdToImpersonate}
            impersonateParentUser={impersonateParentUser}
          />
        </>
      )}
    </div>
  )
}

export const User = connect(({ userProfile }: any) => userProfile, {
  fetchChildUsers,
  getParentUserProfileDetails,
  getParentDriverLocations,
})(UserComponent)
